/**
 * Characters that spreadsheet apps (Excel, Google Sheets, LibreOffice Calc)
 * may interpret as the start of an executable formula when a CSV is opened.
 *
 * Per OWASP CSV Injection guidance, escape any cell that starts with one of
 * these characters, OR with whitespace followed by one of these characters
 * (since some clients strip leading whitespace before evaluating formulas):
 *
 *   `=`  `+`  `-`  `@`   formula prefixes
 *   `\t` `\r`           treated as formula triggers by Excel paste handling
 *
 * Refs: https://owasp.org/www-community/attacks/CSV_Injection
 */
const CSV_FORMULA_PATTERN = /^(?:[=+\-@\t\r]|\s+[=+\-@])/;

/**
 * Escape a single cell value to prevent CSV formula injection.
 *
 * If the value is a string that starts with a spreadsheet formula trigger,
 * prefix it with a single quote. The quote is interpreted by Excel as a
 * text marker and stripped on render; Google Sheets and LibreOffice may
 * render it literally, which is acceptable as a security/UX trade-off.
 *
 * Non-string values pass through unchanged so numeric, boolean, and null
 * cells are preserved.
 */
export function escapeCsvFormulaValue<T>(value: T): T | string {
    if (typeof value !== 'string') {
        return value;
    }

    if (CSV_FORMULA_PATTERN.test(value)) {
        return `'${value}`;
    }

    return value;
}

/**
 * Apply CSV formula escaping to every cell across a set of rows.
 *
 * Returns a new array of new row objects; the input is never mutated so
 * upstream React state and selectors stay stable.
 */
export function escapeCsvFormulaRows(
    rows: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
    return rows.map((row) => {
        const escaped: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(row)) {
            escaped[key] = escapeCsvFormulaValue(value);
        }

        return escaped;
    });
}
